import { Box, Stack } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { VirtualItem } from "@tanstack/react-virtual";
import { useEffect, useRef } from "react";
import usePaginator from "../../../hooks/usePaginator";
import { scrollbarStyle } from "../../../shared/dashboard";
import { Dashboard, Workspace } from "../../../types/files";
import { collectionKey } from "../../organisms/dashboards/DashboardsView";
import { FallbackLoading } from "../../templates/loader";
import NoDashboardsFoundView from "../dashboards-empty/NoDashboardsFoundView";
import DashboardsTableHeader from "../dashboards-table/DashboardsTableHeader";
import DashboardsTableRow from "../dashboards-table/DashboardsTableRow";

const useStyles = makeStyles((theme) => ({
    container: {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
    },
    headers: {
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'smooth',
        ...scrollbarStyle,
    },
    //
    table: {
        width: '100%',
        height: 'calc(100vh - 240px)',
    },
    header: {
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'auto',
        borderBottom: `1px solid ${theme.colors.neutral['400']}`,
        ...scrollbarStyle,
    },
    rows: {
        position: 'relative',
        minWidth: '100%',
        width: 'fit-content',
        height: '100%',
        overflow: 'auto',
        scrollBehavior: 'smooth',
        ...scrollbarStyle,
    },
    row: {
        position: 'absolute',
        top: 0,
        left: 0,
        minWidth: '100%',
        width: 'fit-content',
        height: 'auto',
        borderTop: `1px solid ${theme.colors.neutral['400']}`,
        background: `#fff`,
        "&:hover": {
            background: `rgb(246, 251, 251, 0.8)`,
        },
    },
}));

const TableHeaders = ['Name', 'Stage', 'Sectors', 'Location', '', 'Added', ''];

const SharedByDealOwner: React.FC<{
    workspace?: Workspace,
    dashboards: Dashboard[],
    loading?: boolean,
}> = ({ workspace, dashboards, loading }) => {
    const classes = useStyles();

    const virtualRowsRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);

    const syncScroll = (e?: Event) => {
        e?.preventDefault();

        if (headerRef.current && virtualRowsRef.current) 
            headerRef.current.scrollLeft = virtualRowsRef.current.scrollLeft;
    };

    const { allRows, rowVirtualizer, hasNextPage } = usePaginator({
        key: 'shared-by-deal-owner-63',
        source: dashboards,
        limit: 20,
        itemHeight: 60,
        virtualRowsRef,
    });

    useEffect(() => {        
        const timeout = setTimeout(() => {
            clearTimeout(timeout);
            if (!headerRef.current || !virtualRowsRef.current) return;
    
            virtualRowsRef.current.addEventListener("scroll", syncScroll);
            headerRef.current.addEventListener("wheel", syncScroll, { passive: false });
            headerRef.current.addEventListener("touchmove", syncScroll, { passive: false });
        }, 1000);
    
        return () => {
            clearTimeout(timeout);
            if (!headerRef.current || !virtualRowsRef.current) return;
    
            // eslint-disable-next-line 
            virtualRowsRef?.current?.removeEventListener("scroll", syncScroll);
            headerRef?.current?.removeEventListener("wheel", syncScroll);
            // eslint-disable-next-line 
            headerRef?.current?.removeEventListener("touchmove", syncScroll);
        };
    }, []);
    
    if (!workspace)
        return (<></>);

    return (<>
        <Stack className={classes.container} alignItems="space-between" justifyContent="stretch">
            {loading ? (<FallbackLoading />) : (
                <Stack className={classes.table}>
                    {!!dashboards.length ? (<>
                        <Stack alignItems="flex-start" justifyContent="flex-start" height="100%">
                            <Box width="100%" height="fit-content" overflow="hidden">
                                <Box className={classes.header} ref={headerRef}>
                                    <DashboardsTableHeader
                                        columns={TableHeaders}
                                        actions={['Delete']}
                                        xAdjust={80}
                                        multiAction />
                                </Box>
                            </Box>
                            <Box width="100%" height="100%" overflow="hidden">
                                <Box className={classes.rows} ref={virtualRowsRef} 
                                    sx={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
                                    {rowVirtualizer.getVirtualItems().map((virtualRow: VirtualItem) => (
                                        <Box className={classes.row} 
                                            sx={{  height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)`, }}
                                            key={'shared-by-deal-owner-148-' + virtualRow.index} >
                                            {(virtualRow.index > allRows.length - 1) && hasNextPage 
                                                ? (<FallbackLoading />)
                                                : (<DashboardsTableRow
                                                        dashboard={allRows[virtualRow.index]}
                                                        workspace={workspace ?? undefined}
                                                        columns={TableHeaders}
                                                        collectionKey={collectionKey}
                                                        isPublic />)}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Stack>
                    </>) : (<NoDashboardsFoundView noCompaniesYet />)}
                </Stack>
            )}
        </Stack>
    </>);
}

export default SharedByDealOwner;